<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import PlotlyLineChart from '@/components/plotly-chart/PlotlyLineChart.vue'
import { dikeProfilePlotlyMixin } from '@/mixins/dikeProfilePlotlyMixin'
import { annotationMixin } from '@/mixins/annotationMixin'

const props = defineProps({ dikeProfiles: {} })

const emit = defineEmits(['add-dirty'])

const store = useStore()
const storeNameSpace = 'topo_profile'

const { dikeProfilePlotDataWithHighlight } = dikeProfilePlotlyMixin()
const { annotations } = annotationMixin(store)

const currentTopoProfile = computed(() => store.state.topoProfile.currentItem)
const items = computed(() => store.state.topoProfilePoint.items)
const currentItem = computed(() => store.state.topoProfilePoint.currentItem)
const currentSchematisation = computed(
  () => store.state.stabSchematisation.currentItem
)
const currentStabSchematisationStage = computed(
  () => store.state.stabSchematisationStage.currentItem
)

const topoPlotData = computed(() => store.getters['topoProfilePoint/getPlotData'])
const isLocked = computed(() => store.getters['stabSchematisation/isLockedCurrent'])


const getChartYAxisRange = computed(() => (id) => {
  return store.getters['topoProfilePoint/getChartYAxisRange'](id)
})

const refPlot = computed(
  () => `${storeNameSpace}-${currentStabSchematisationStage.value?.id}`
)

const currentItemAnnotation = computed(() => {
  return currentItem.value
    ? {
        x: currentItem.value.geometry.coordinates[0],
        y: currentItem.value.geometry.coordinates[1],
        xref: 'x',
        yref: 'y',
        showarrow: true,
        arrowhead: 7,
        arrowsize: 1.25,
        arrowcolor: '#0186A7',
        ax: 0,
        ay: 10,
        hideToggle: false
      }
    : null
})

const combinedPlotData = computed(() => {
  if (!currentSchematisation.value || !currentTopoProfile.value) {
    return []
  }

  const data = dikeProfilePlotDataWithHighlight(props.dikeProfiles,
  ).concat(topoPlotData.value())

  for (const trace of data) {
    trace.type = 'scattergl'
  }

  return data
})

const setActiveItem = (event) => {
  const eventItem = event.points[0]
  const item = items.value[eventItem.pointIndex]
  store.dispatch('topoProfilePoint/setCurrentItem', item)
}

const addPointOnClick = (...args) =>
  store.dispatch('topoProfilePoint/addPointOnClick', ...args)

const addNewPoint = async ({ x, y }) => {
  const newPoint = await addPointOnClick({
    coordinates: [x, y].map((value) => Math.round(value * 100) / 100),
    topoProfileId: currentTopoProfile.value.id
  })

  const pointFields = ['0', '1', 'name']
  for (const pointField of pointFields) {
    emit('add-dirty', newPoint, pointField)
  }
}
</script>

<template>
  <PlotlyLineChart
    class="fill-height"
    :responsive="false"
    :data="combinedPlotData"
    :showAnimate="false"
    :annotations="[...annotations, currentItemAnnotation]"
    :refPlot="refPlot"
    :ref="refPlot"
    :yAxisRange="getChartYAxisRange(currentTopoProfile?.id)"
    :clickEvent="true"
    @action="setActiveItem"
    :show-add-point="!isLocked"
    @add-point="addNewPoint"
  />
</template>
